import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material'
import { CheckCircleOutline, SmartToy } from '@mui/icons-material'
import {
  mcpConnectionsApi,
  readAuthorizeParams,
  type McpAuthorizeParams,
  type McpConsentRequest,
} from '@/api/mcpConnections'
import { useAuthStore } from '@/stores/authStore'
import { ROUTES } from '@/utils/constants'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'
import { rememberPendingRedirect } from '@/utils/pendingRedirect'

/** Петлевые хосты, которым разрешён http: локальный клиент по RFC 8252. */
const LOOPBACK_HOSTS = ['127.0.0.1', '[::1]', 'localhost']

/**
 * Можно ли уводить браузер по этому адресу.
 *
 * Только http(s), и http — лишь на петлю. `javascript:` и `data:` исполнились бы
 * в origin SPA, где в localStorage лежит JWT.
 */
function isSafeRedirect(url: string): boolean {
  try {
    const parsed = new URL(url)

    if (parsed.protocol === 'https:') return true

    return parsed.protocol === 'http:' && LOOPBACK_HOSTS.includes(parsed.hostname)
  } catch {
    return false
  }
}

/**
 * Экран согласия на подключение AI-агента.
 *
 * Стандартный экран Passport — серверная страница на домене API под сессией, но
 * на api.easya.ru нет ни формы логина, ни сессий, и светить этот домен
 * пользователю не нужно. Поэтому `/oauth/authorize` на API редиректит сюда, а
 * исходные параметры OAuth возвращаются серверу на каждом шаге: запрос
 * авторизации нигде не хранится и проверяется заново.
 */
export function OAuthAuthorizePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [search] = useSearchParams()
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)

  const params = useMemo<McpAuthorizeParams | null>(() => readAuthorizeParams(search), [search])

  const [consent, setConsent] = useState<McpConsentRequest | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Неавторизованного отправляем на логин и возвращаем обратно вместе с
  // параметрами — иначе после входа подключение придётся начинать заново.
  useEffect(() => {
    if (!isAuthenticated) {
      rememberPendingRedirect(`${ROUTES.OAUTH_AUTHORIZE}?${search.toString()}`)
      navigate(ROUTES.LOGIN, { replace: true })
    }
  }, [isAuthenticated, navigate, search])

  useEffect(() => {
    if (!isAuthenticated) return

    if (params === null) {
      setError(t('mcpConsent.invalidRequest'))
      setIsLoading(false)
      return
    }

    let cancelled = false

    mcpConnectionsApi
      .authorizeRequest(params)
      .then((data) => {
        if (!cancelled) setConsent(data)
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(displayErrorMessage(getErrorMessage(err), t('mcpConsent.invalidRequest')))
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [isAuthenticated, params, t])

  const decide = async (approve: boolean) => {
    if (params === null) return

    try {
      setIsSubmitting(true)
      setError(null)

      const redirectUrl = approve
        ? await mcpConnectionsApi.approve(params)
        : await mcpConnectionsApi.deny(params)

      // Схему проверяет и сервер, но это последний рубеж перед присваиванием в
      // location: адрес приходит из redirect_uri клиента, а регистрация клиентов
      // открыта. `javascript:` здесь исполнился бы в origin SPA.
      if (!isSafeRedirect(redirectUrl)) {
        setError(t('mcpConsent.unsafeRedirect'))
        setIsSubmitting(false)
        return
      }

      // Возврат уходит на адрес клиента (localhost агента), поэтому это выход
      // из SPA, а не навигация внутри роутера.
      window.location.href = redirectUrl
    } catch (err: unknown) {
      setError(displayErrorMessage(getErrorMessage(err), t('mcpConsent.decisionFailed')))
      setIsSubmitting(false)
    }
  }

  if (!isAuthenticated) return null

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
      <Card sx={{ maxWidth: 560, width: '100%' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <SmartToy color="primary" />
            <Typography variant="h5" component="h1" fontWeight={700}>
              {t('mcpConsent.title')}
            </Typography>
          </Box>

          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {consent && !isLoading && (
            <>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {t('mcpConsent.intro', { client: consent.client.name })}
              </Typography>

              {consent.client.redirect_host && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('mcpConsent.redirectHost', { host: consent.client.redirect_host })}
                </Typography>
              )}

              {consent.already_granted && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('mcpConsent.alreadyGranted')}
                </Alert>
              )}

              <Divider sx={{ my: 2 }} />

              {consent.groups.length > 0 ? (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {t('mcpConsent.accessTitle')}
                  </Typography>
                  <List dense disablePadding>
                    {consent.groups.map((group) => (
                      <ListItem key={group.key} disableGutters>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleOutline fontSize="small" color="success" />
                        </ListItemIcon>
                        <ListItemText primary={group.label} />
                      </ListItem>
                    ))}
                  </List>
                </>
              ) : (
                // Пустой набор — тариф не включает MCP или роль не та: кнопки
                // «Разрешить» тут быть не должно, вместо неё путь к тарифу.
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {t('mcpConsent.noAccess')}
                </Alert>
              )}

              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {t('mcpConsent.expiresIn', { days: consent.expires_in_days })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('mcpConsent.revokeHint')}
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, mt: 3, flexWrap: 'wrap' }}>
                {consent.groups.length > 0 ? (
                  <Button
                    variant="contained"
                    onClick={() => decide(true)}
                    disabled={isSubmitting}
                  >
                    {t('mcpConsent.allow')}
                  </Button>
                ) : (
                  <Button variant="contained" onClick={() => navigate(ROUTES.PROFILE_SUBSCRIPTION)}>
                    {t('mcpConsent.goToPlan')}
                  </Button>
                )}
                <Button variant="outlined" onClick={() => decide(false)} disabled={isSubmitting}>
                  {t('mcpConsent.deny')}
                </Button>
              </Box>
            </>
          )}

          {!consent && !isLoading && !error && (
            <Chip label={t('mcpConsent.invalidRequest')} color="warning" />
          )}
        </CardContent>
      </Card>
    </Box>
  )
}
